import { create } from 'zustand';
import { OnboardingData, ProductData, VisualContent, AdditionalOffers, VisualStyle, SalesAngle, ContextData, MultiFunnelData, FunnelConfig, FunnelSection, LegacyTemplate } from '../types/onboarding';
import { axiosInstanceConversationalEngine, axiosInstanceWebsite } from '../services/instance';
import {
  SectionTemplate,
  getSectionTemplates,
  getOnboardingCards,
  postBulkFunnelsEstimate,
  type OnboardingCardBinding,
} from '../services/funnels';
import { DesignStructureResponse, DesignStructurePayload, DesignStructureRecommendedSection, selectDesignStructure, SalesAngleSelectResponse, selectSalesAngle } from '../services/designStructure/designStructureService';
import type { AdStyle } from '../feature/ads/cretives/components/modals/steps/adStudio/types';
import { templatesService } from '../feature/ads/cretives/services/templates.service';
import { MOCK_STYLES } from '../feature/ads/cretives/components/modals/steps/adStudio/stylesData';
import { creativesService } from '../feature/ads/cretives/services/creatives.service';
import { getOnboardingBindingOptionId, isSelectableOnboardingCard, type OnboardingTemplateDesign, type OnboardingTemplateOption } from '../templateStudio/onboardingTemplateOptions';
import { isSingleCheckoutQuantityMode } from '../templateStudio/templateFamilyMetadata.ts';
import { invalidatePreparedTemplateSections } from '../templateStudio/onboardingTemplateSelection.ts';
import {
  ONBOARDING_VIDEO_REVIEWS_GIFS_CREDITS_PER_FUNNEL,
  getOnboardingVideoReviewsGifsCredits,
} from '../const/onboardingVideoReviews';

let estimatedCostRequestGeneration = 0;
const invalidateEstimatedCostRequest = () => {
  estimatedCostRequestGeneration += 1;
};

export enum UploadedMethodType {
  LINK = 'link',
  MANUAL = 'manual',
  PREVIOUS = 'previous',
  NONE = 'none',
}

export interface PreviousProduct {
  id: string;
  name: string;
  price: number | string;
  country_code?: string;
  currency_code?: string;
  description?: string;
  images?: string[];
  inventory_type?: 'own' | 'dropshipping';
}

const initialProductData: ProductData = {
  name: '',
  price: '',
  countryCode: 'CO',
  currencyCode: 'COP',
  details: '',
  warranty: '30days',
  // Contra entrega por defecto: es lo que el backend ya hace de facto (sin proveedor
  // online configurado, la normalizacion cae a COD). Asi no bloquea el onboarding.
  paymentMethod: 'cod',
  freeShipping: true,
  isManualEntry: false,
  productLink: '',
  customWarrantyDays: '',
  uploadedMethod: UploadedMethodType.LINK,
  variants: null,
};

const initialVisualContent: VisualContent = {
  imagesBase64: [],
  images: [],
  selectedAngle: [],
};

const initialAdditionalOffers: AdditionalOffers = {
  bumpOffer: {
    enabled: false,
    productName: '',
    price: '',
    image: "",
    url_images: "",
    description: '',
    isManualEntry: false,
    productLink: '',
    externalId: null,
    providerId: null,
    uploadedMethod: UploadedMethodType.NONE,
  },
  upsell: {
    enabled: false,
    productName: '',
    price: '',
    image: undefined,
    products: [],
  },
  downsell: {
    enabled: false,
    discountPercentage: '',
  },
};

const initialVisualStyle: VisualStyle = {
  colorPalette: '',
  brandName: '',
  brandId: null,
  brandContext: null,
  useAiLogo: true,
};

const initialContextData: ContextData = {
  pain_detection: '',
  buyer_detection: '',
  recommended_sales_angles: [],
};

// Standard structural sections (always available regardless of design)
// Order: CTA1 right after cover (0), CTA2 mid-funnel, CTA3 late-funnel, Guarantee, FAQ last
const STANDARD_SECTIONS: FunnelSection[] = [
  { id: 'cta-1', name: 'Botón de compra 1', icon: 'shopping-cart', enabled: true, order: 0, isStandard: true },
  { id: 'warranty', name: 'Garantía', icon: 'shield', enabled: true, order: 1, isStandard: true },
  { id: 'cta-2', name: 'Botón de compra 2', icon: 'shopping-cart', enabled: true, order: 2, isStandard: true },
  { id: 'cta-3', name: 'Botón de compra 3', icon: 'shopping-cart', enabled: true, order: 3, isStandard: true },
  { id: 'faq', name: 'Preguntas frecuentes', icon: 'help-circle', enabled: true, order: 4, isStandard: true },
];

// Section types that should be hidden and always disabled in onboarding.
// Kept in code for potential future re-enablement.
const HIDDEN_SECTION_TYPES = new Set(['selector-cantidades', 'video_reviews']);
const MIN_TEMPLATE_STUDIO_IMAGE_SECTIONS = 8;

type FunnelTemplateDesignSelection = Partial<OnboardingTemplateDesign> & {
  previewUrl?: string | null;
  universalStyleSelection?: OnboardingTemplateOption['universalStyleSelection'];
};

const createUniversalCreationRequestId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `universal-${Date.now()}-${Math.random().toString(36).slice(2)}`;

const getTemplateSectionOrder = (section: SectionTemplate, index: number) => {
  const sort = Number(section.metadata?.sort);
  if (Number.isFinite(sort)) return sort;
  return 999 + index;
};

const resolveSectionInstructions = (metadata?: Record<string, unknown> | null) => {
  if (!metadata) return undefined;
  const current = typeof metadata.section_instructions === 'string' ? metadata.section_instructions.trim() : '';
  if (current) return current;

  const legacyParts = [
    typeof metadata.copy_prompt === 'string' ? metadata.copy_prompt.trim() : '',
    typeof metadata.content_rules === 'string' ? metadata.content_rules.trim() : '',
  ].filter(Boolean).join('\n\n');

  return legacyParts || undefined;
};

// Map section_type to a readable display name
const SECTION_TYPE_DISPLAY_NAMES: Record<string, string> = {
  'portada': 'Portada',
  'beneficios-destacados': 'Beneficios Destacados',
  'beneficios': 'Beneficios',
  'selector-cantidades': 'Selector de Cantidades',
  'antes-y-despues': 'Antes y Después',
  'visualizaciones-datos': 'Visualización de Datos',
  'hecho-de': 'Hecho de',
  'como-usar': 'Cómo Usar',
  'calificacion': 'Calificación',
  'testimonios': 'Testimonios',
  'objeciones': 'Objeciones',
  'tabla-comparativa': 'Tabla Comparativa',
  'ebook': 'Ebook / Lead Magnet',
};

type OnboardingStore = {
  data: OnboardingData;
  setStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  updateProductData: (data: Partial<ProductData>) => void;
  updateVisualContent: (data: Partial<VisualContent>) => void;
  updateAdditionalOffers: (data: Partial<AdditionalOffers>) => void;
  updateVisualStyle: (data: Partial<VisualStyle>) => void;
  updateAdditionalUserInstructions: (value: string) => void;
  resetOnboardingData: () => void;
  resetProductDerivedState: () => void;
  salesAngles: SalesAngle[];
  hasFetchedSalesAngles: boolean;
  setSalesAngles: (salesAngles: SalesAngle[]) => void;
  setHasFetchedSalesAngles: (hasFetchedSalesAngles: boolean) => void;
  previousProducts: PreviousProduct[];
  setPreviousProducts: (products: PreviousProduct[]) => void;
  isPreviousProductsLoading: boolean;
  setIsPreviousProductsLoading: (isPreviousProductsLoading: boolean) => void;
  previousProductsSearch: string;
  previousProductsPage: number;
  previousProductsTotalPages: number;
  hasFetchedPreviousProducts: boolean;
  fetchPreviousProducts: (search?: string, page?: number, size?: number) => Promise<PreviousProduct[]>;
  searchPreviousProducts: (search: string, page?: number, size?: number) => Promise<PreviousProduct[]>;
  contextData: ContextData;
  updateContextData: (data: Partial<ContextData>) => void;
  priceErrors: { main?: string; bump?: string; upsell?: string };
  setPriceErrors: (errors: { main?: string; bump?: string; upsell?: string }) => void;
  clearPriceErrors: () => void;
  isCustomSectionsOnly: boolean;
  setIsCustomSectionsOnly: (value: boolean) => void;
  showCustomSectionsToggle: boolean;
  setShowCustomSectionsToggle: (value: boolean) => void;
  multiFunnelData: MultiFunnelData;
  setFunnelCount: (count: number) => void;
  updateFunnelConfig: (funnelIndex: number, config: Partial<FunnelConfig>) => void;
  updateFunnelSection: (funnelIndex: number, sectionId: string, updates: Partial<FunnelSection>) => void;
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => void;
  initializeFunnels: (count: number, countryCode?: string, currencyCode?: string) => void;
  addFunnel: () => void;
  removeFunnel: (funnelIndex: number) => void;
  setFunnelDesign: (funnelIndex: number, templateId: string, templateType: string, recommendedSections?: DesignStructureRecommendedSection[], templateDesign?: FunnelTemplateDesignSelection) => Promise<void>;
  isFetchingDesignSections: boolean;
  sectionTemplates: SectionTemplate[];
  isFetchingTemplates: boolean;
  hasFetchedTemplates: boolean;
  fetchSectionTemplates: () => Promise<void>;
  onboardingBindings: OnboardingCardBinding[];
  isFetchingBindings: boolean;
  hasFetchedBindings: boolean;
  fetchOnboardingBindings: () => Promise<void>;
  // Design structure recommendation (AI-powered)
  designRecommendation: DesignStructureResponse | null;
  isFetchingDesignRecommendation: boolean;
  hasFetchedDesignRecommendation: boolean;
  fetchDesignRecommendation: (templateTypes?: string[], force?: boolean) => Promise<DesignStructureResponse | null>;
  resetDesignRecommendation: () => void;
  // Sales angle recommendation (AI-powered)
  salesAngleRecommendation: SalesAngleSelectResponse | null;
  isFetchingSalesAngleRecommendation: boolean;
  hasFetchedSalesAngleRecommendation: boolean;
  fetchSalesAngleRecommendation: (rawAngles: { name: string; description: string; complementary_angles?: string[] }[]) => Promise<void>;
  // Ad creative templates
  adTemplates: AdStyle[];
  isFetchingAdTemplates: boolean;
  hasFetchedAdTemplates: boolean;
  isRankingTemplates: boolean;
  hasRankedTemplates: boolean;
  rankedTemplateProductKey: string | null;
  isRankingPageTemplates: boolean;
  hasRankedPageTemplates: boolean;
  rankedPageTemplateProductKey: string | null;
  isRankingAdTemplates: boolean;
  hasRankedAdTemplates: boolean;
  rankedAdTemplateProductKey: string | null;
  fetchAdTemplates: () => Promise<void>;
  rankOnboardingTemplates: () => Promise<void>;
  toggleFunnelAdTemplate: (funnelIndex: number, templateId: string) => void;
  // Estimated cost
  estimatedCost: {
    total_cost: number;
    funnels_cost: number;
    creatives_cost: number;
    video_reviews_gifs_cost?: number;
    video_reviews_gifs_credits_per_funnel?: number;
  } | null;
  isFetchingEstimatedCost: boolean;
  fetchEstimatedCost: () => Promise<void>;
  // Legacy template selection (multiple)
  selectedLegacyTemplates: LegacyTemplate[];
  toggleLegacyTemplate: (template: LegacyTemplate) => void;
  clearLegacyTemplates: () => void;
  selectedOnboardingTemplateIds: string[];
  setSelectedOnboardingTemplateIds: (templateIds: string[]) => void;
};

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  data: {
    step: 1,
    productData: initialProductData,
    visualContent: initialVisualContent,
    additionalOffers: initialAdditionalOffers,
    visualStyle: initialVisualStyle,
    isManualEntry: false,
    productLink: '',
    additionalUserInstructions: '',
  },
  salesAngles: [],
  hasFetchedSalesAngles: false,
  previousProducts: [],
  isPreviousProductsLoading: false,
  previousProductsSearch: '',
  previousProductsPage: 0,
  previousProductsTotalPages: 0,
  hasFetchedPreviousProducts: false,
  setIsPreviousProductsLoading: (isPreviousProductsLoading: boolean) => set({ isPreviousProductsLoading }),
  contextData: initialContextData,
  setStep: (step) => set((state) => ({ data: { ...state.data, step } })),
  nextStep: () => set((state) => {
    // All users now go through the unified 5-step flow (product → offers → brand → funnelConfig → summary)
    const maxStep = 5;
    return { data: { ...state.data, step: Math.min(state.data.step + 1, maxStep) } };
  }),
  previousStep: () => set((state) => ({ data: { ...state.data, step: Math.max(state.data.step - 1, 1) } })),
  updateProductData: (productData) => 
    set((state) => {
      const newProductData = { ...state.data.productData, ...productData };
      return { 
        data: { 
          ...state.data, 
          productData: newProductData 
        }
      };
    }),
  updateVisualContent: (visualContent: Partial<VisualContent>) => 
    set((state) => {
      const newVisualContent = { ...state.data.visualContent, ...visualContent };
      return {
        data: { 
          ...state.data, 
          visualContent: newVisualContent
        }
      };
    }),
  updateAdditionalOffers: (additionalOffers) => 
    set((state) => {
      const updatedOffers = { ...state.data.additionalOffers };
      
      if (additionalOffers.bumpOffer) {
        updatedOffers.bumpOffer = {
          ...updatedOffers.bumpOffer,
          ...additionalOffers.bumpOffer
        };
      }
      
      if (additionalOffers.upsell) {
        updatedOffers.upsell = {
          ...updatedOffers.upsell,
          ...additionalOffers.upsell,
          // Handle nested products array if present
          products: additionalOffers.upsell.products || updatedOffers.upsell.products
        };
      }
      
      // Handle downsell specifically
      if (additionalOffers.downsell) {
        updatedOffers.downsell = {
          ...updatedOffers.downsell,
          ...additionalOffers.downsell
        };
      }
      
      return { 
        data: { 
          ...state.data, 
          additionalOffers: updatedOffers 
        } 
      };
    }),
  updateVisualStyle: (visualStyle) =>
    set((state) => ({
      data: {
        ...state.data,
        visualStyle: { ...state.data.visualStyle, ...visualStyle }
      }
    })),
  updateAdditionalUserInstructions: (value: string) =>
    set((state) => ({
      data: {
        ...state.data,
        // Trim trailing whitespace at save-time only if there is non-whitespace content,
        // so the user can type spaces while still authoring without ghost-saving "   ".
        additionalUserInstructions: value,
      },
    })),
  resetOnboardingData: () => set({
    data: {
      step: 1,
      productData: initialProductData,
      visualContent: initialVisualContent,
      additionalOffers: initialAdditionalOffers,
      visualStyle: initialVisualStyle,
      isManualEntry: false,
      productLink: '',
      additionalUserInstructions: '',
    },
    salesAngles: [],
    hasFetchedSalesAngles: false,
    previousProducts: [],
    isPreviousProductsLoading: false,
    previousProductsSearch: '',
    previousProductsPage: 0,
    previousProductsTotalPages: 0,
    hasFetchedPreviousProducts: false,
    contextData: initialContextData,
    priceErrors: {},
    isCustomSectionsOnly: false,
    multiFunnelData: { funnelCount: 3, funnels: [] },
    designRecommendation: null,
    isFetchingDesignRecommendation: false,
    hasFetchedDesignRecommendation: false,
    salesAngleRecommendation: null,
    isFetchingSalesAngleRecommendation: false,
    hasFetchedSalesAngleRecommendation: false,
    adTemplates: [],
    isFetchingAdTemplates: false,
    hasFetchedAdTemplates: false,
    isRankingTemplates: false,
    hasRankedTemplates: false,
    rankedTemplateProductKey: null,
    isRankingPageTemplates: false,
    hasRankedPageTemplates: false,
    rankedPageTemplateProductKey: null,
    isRankingAdTemplates: false,
    hasRankedAdTemplates: false,
    rankedAdTemplateProductKey: null,
    estimatedCost: null,
    isFetchingEstimatedCost: false,
    selectedLegacyTemplates: [],
    selectedOnboardingTemplateIds: [],
  }),
  resetProductDerivedState: () => set((state) => ({
    data: {
      ...state.data,
      visualContent: {
        ...state.data.visualContent,
        selectedAngle: [],
      },
      visualStyle: {
        ...state.data.visualStyle,
        brandName: '',
      },
    },
    salesAngles: [],
    hasFetchedSalesAngles: false,
    contextData: initialContextData,
    priceErrors: {},
    multiFunnelData: { funnelCount: 3, funnels: [] },
    designRecommendation: null,
    isFetchingDesignRecommendation: false,
    hasFetchedDesignRecommendation: false,
    salesAngleRecommendation: null,
    isFetchingSalesAngleRecommendation: false,
    hasFetchedSalesAngleRecommendation: false,
    isRankingTemplates: false,
    hasRankedTemplates: false,
    rankedTemplateProductKey: null,
    isRankingPageTemplates: false,
    hasRankedPageTemplates: false,
    rankedPageTemplateProductKey: null,
    isRankingAdTemplates: false,
    hasRankedAdTemplates: false,
    rankedAdTemplateProductKey: null,
    estimatedCost: null,
    isFetchingEstimatedCost: false,
    selectedLegacyTemplates: [],
    selectedOnboardingTemplateIds: [],
  })),
  priceErrors: {},
  setPriceErrors: (errors) => set({ priceErrors: errors }),
  clearPriceErrors: () => set({ priceErrors: {} }),
  isCustomSectionsOnly: false,
  setIsCustomSectionsOnly: (value: boolean) => set({ isCustomSectionsOnly: value }),
  showCustomSectionsToggle: false,
  setShowCustomSectionsToggle: (value: boolean) => set({ showCustomSectionsToggle: value }),
  multiFunnelData: { funnelCount: 3, funnels: [] },
  setFunnelCount: (count: number) => set((state) => ({
    multiFunnelData: { ...state.multiFunnelData, funnelCount: count }
  })),
  updateFunnelConfig: (funnelIndex: number, config: Partial<FunnelConfig>) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = { ...funnels[funnelIndex], ...config };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  updateFunnelSection: (funnelIndex: number, sectionId: string, updates: Partial<FunnelSection>) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      const isUniversal = funnels[funnelIndex].universalStyleEngineVersion === 'universal_slots_v1';
      funnels[funnelIndex] = {
        ...funnels[funnelIndex],
        // The idempotency key identifies the complete frozen creation
        // selection, including the user's final enabled role set and order.
        // If that selection changes, the next submit is a new command; normal
        // retries without another edit keep the same key.
        ...(isUniversal
          ? { universalCreationRequestId: createUniversalCreationRequestId() }
          : {}),
        sections: funnels[funnelIndex].sections.map(s =>
          s.id === sectionId ? { ...s, ...updates } : s
        )
      };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  reorderFunnelSections: (funnelIndex: number, sections: FunnelSection[]) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (funnels[funnelIndex]) {
      funnels[funnelIndex] = {
        ...funnels[funnelIndex],
        ...(funnels[funnelIndex].universalStyleEngineVersion === 'universal_slots_v1'
          ? { universalCreationRequestId: createUniversalCreationRequestId() }
          : {}),
        sections,
      };
    }
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  initializeFunnels: (count: number, countryCode = 'CO', currencyCode = 'COP') => {
    invalidateEstimatedCostRequest();
    set((state) => {
      const brandName = state.data.visualStyle.brandName?.trim() || 'Embudo';
      const previousFunnels = state.multiFunnelData.funnels;
      const previousIncludeVideoReviewsGifs = previousFunnels.some(funnel => funnel.includeVideoReviewsGifs);
      const funnels: FunnelConfig[] = Array.from({ length: count }, (_, i) => ({
        id: `funnel-${i + 1}`,
        name: `${brandName} ${i + 1}`,
        designTemplate: null,
        designTemplateType: null,
        designTemplateContractVersion: null,
        designTemplateFamilyMetadata: null,
        sections: STANDARD_SECTIONS.map((s, idx) => ({ ...s, order: idx })),
        selectedAngles: [],
        selectedAdTemplates: [],
        countryCode,
        currencyCode,
        promotion: null,
        includeVideoReviewsGifs: previousFunnels[i]?.includeVideoReviewsGifs ?? previousIncludeVideoReviewsGifs,
      }));
      return {
        multiFunnelData: { funnelCount: count, funnels },
        estimatedCost: null,
        isFetchingEstimatedCost: false,
      };
    });
  },
  addFunnel: () => set((state) => {
    const { funnels } = state.multiFunnelData;
    if (funnels.length >= 5) return state;
    const brandName = state.data.visualStyle.brandName?.trim() || 'Embudo';
    const newIndex = funnels.length + 1;
    const newFunnel: FunnelConfig = {
      id: `funnel-${Date.now()}`,
      name: `${brandName} ${newIndex}`,
      designTemplate: null,
      designTemplateType: null,
      designTemplateContractVersion: null,
      designTemplateFamilyMetadata: null,
      sections: STANDARD_SECTIONS.map((s, idx) => ({ ...s, order: idx })),
      selectedAngles: [],
      selectedAdTemplates: [],
      countryCode: funnels[0]?.countryCode || 'CO',
      currencyCode: funnels[0]?.currencyCode || 'COP',
      promotion: null,
      includeVideoReviewsGifs: funnels[0]?.includeVideoReviewsGifs ?? false,
    };
    const newFunnels = [...funnels, newFunnel];
    return { multiFunnelData: { funnelCount: newFunnels.length, funnels: newFunnels } };
  }),
  removeFunnel: (funnelIndex: number) => set((state) => {
    const { funnels } = state.multiFunnelData;
    if (funnels.length <= 1) return state;
    const newFunnels = funnels.filter((_, i) => i !== funnelIndex);
    return { multiFunnelData: { funnelCount: newFunnels.length, funnels: newFunnels } };
  }),
  isFetchingDesignSections: false,
  setFunnelDesign: async (funnelIndex: number, templateId: string, templateType: string, recommendedSections?: DesignStructureRecommendedSection[], templateDesign?: FunnelTemplateDesignSelection) => {
    set({ isFetchingDesignSections: true });
    try {
      const universalSelection = templateDesign?.universalStyleSelection;
      if (universalSelection) {
        set((state) => {
          const funnels = [...state.multiFunnelData.funnels];
          const current = funnels[funnelIndex];
          if (!current) return { isFetchingDesignSections: false };
          funnels[funnelIndex] = {
            ...current,
            designTemplate: templateId,
            designTemplateType: templateType,
            designTemplateName: templateDesign?.templateName ?? null,
            designTemplatePreviewUrl: templateDesign?.previewUrl ?? null,
            designTheme: null,
            designStyleSheet: null,
            designPalette: null,
            designPrimaryColor: null,
            designSecondaryColor: null,
            designCoverStyle: null,
            designTemplateContractVersion: null,
            designTemplateFamilyMetadata: null,
            universalStyleEngineVersion: universalSelection.styleEngineVersion,
            universalCatalogReleaseId: universalSelection.catalogReleaseId,
            universalPageStyleId: universalSelection.pageStyleId,
            // A template click is a new selection command, even when the user
            // reselects the same style after changing its wizard composition.
            universalCreationRequestId: createUniversalCreationRequestId(),
            sections: universalSelection.wizardDefaultComposition.map((section) => ({
              id: `universal-instance-${section.instanceKey}`,
              name: section.displayName,
              icon: 'layout',
              enabled: section.enabled,
              order: section.order,
              sectionType: section.sectionRole,
              universalInstanceKey: section.instanceKey,
              universalTargetDesignId: section.targetDesignId,
              isStandard: false,
            })),
          };
          return {
            multiFunnelData: { ...state.multiFunnelData, funnels },
            isFetchingDesignSections: false,
          };
        });
        return;
      }
      // Reuse the templates already loaded for onboarding. This avoids a burst
      // of per-template proxy calls while preparing multiple funnels.
      const cachedSections = get().sectionTemplates.filter(section => section.type === templateType);
      const hasCachedDesignSections = cachedSections.some(section => section.section_type !== 'preview');
      const allSections = hasCachedDesignSections
        ? cachedSections
        : await getSectionTemplates('type', templateType);
      const designSections = allSections.filter(s => s.section_type !== 'preview');
      const isHtmlTemplate = designSections.some(s => Boolean(s.metadata?.html_content));

      // Build a lookup for recommended sections (if provided)
      // Design-structure section selection only applies to image templates.
      // HTML templates are authored as complete section sets and should ship all active sections.
      const hasRecommendation = !isHtmlTemplate && Boolean(recommendedSections?.length);
      const visibleDesignSections = designSections
        .filter((s) => !HIDDEN_SECTION_TYPES.has(s.section_type))
        .sort((a, b) => getTemplateSectionOrder(a, 0) - getTemplateSectionOrder(b, 0));
      const recommendedTypesSet = hasRecommendation
        ? new Set((recommendedSections ?? []).map(rs => rs.section_type))
        : null;
      const recommendedOrderMap = hasRecommendation
        ? new Map((recommendedSections ?? []).map(rs => [rs.section_type, rs.order]))
        : null;

      // Convert API sections to FunnelSection format
      // If we have a recommendation, only enable sections that are recommended
      // Hidden section types are excluded entirely from the structure
      const apiSections: FunnelSection[] = visibleDesignSections
        .map((s, idx) => ({
          id: `design-${s.section_type}-${s.id}`,
          name: SECTION_TYPE_DISPLAY_NAMES[s.section_type] || s.section_type,
          icon: 'image',
          enabled: recommendedTypesSet ? recommendedTypesSet.has(s.section_type) : true,
          order: getTemplateSectionOrder(s, idx),
          sectionType: s.section_type,
          url: s.url || undefined,
          isStandard: false,
          hasHtmlContent: Boolean(s.metadata?.html_content),
          templateDbId: s.id,
          imageFormat: typeof s.metadata?.image_format === 'string' ? s.metadata.image_format : undefined,
          colorMode: typeof s.metadata?.color_mode === 'string' ? s.metadata.color_mode : undefined,
          primaryColor: typeof s.metadata?.primary_color === 'string' ? s.metadata.primary_color : undefined,
          backgroundColor: typeof s.metadata?.background_color === 'string' ? s.metadata.background_color : undefined,
          sectionInstructions: resolveSectionInstructions(s.metadata),
          communicationStructure: s.metadata?.communication_structure,
        }));
      // Sort by the template's metadata.sort so sections appear in the intended order
      apiSections.sort((a, b) => a.order - b.order);

      // If the recommendation does not intersect enough with the selected image family,
      // keep the recommended picks and top off from the family's own available sections.
      const isCustomImageTemplate = apiSections.some(s => s.url && !s.hasHtmlContent);
      if (isCustomImageTemplate && recommendedTypesSet) {
        const minEnabledSections = Math.min(MIN_TEMPLATE_STUDIO_IMAGE_SECTIONS, apiSections.length);
        let enabledCount = apiSections.filter(section => section.enabled).length;

        if (enabledCount < minEnabledSections) {
          for (const section of apiSections) {
            if (enabledCount >= minEnabledSections) break;
            if (!section.enabled) {
              section.enabled = true;
              enabledCount += 1;
            }
          }
        }
      }

      // If we have a recommendation, sort sections by their recommended order
      // Enabled (recommended) sections first, sorted by order; disabled sections at the end
      if (recommendedOrderMap) {
        apiSections.sort((a, b) => {
          const aEnabled = a.enabled;
          const bEnabled = b.enabled;
          // Enabled sections come first
          if (aEnabled && !bEnabled) return -1;
          if (!aEnabled && bEnabled) return 1;
          // Both enabled: sort by recommended order
          if (aEnabled && bEnabled) {
            const orderA = a.sectionType ? (recommendedOrderMap.get(a.sectionType) ?? 999) : 999;
            const orderB = b.sectionType ? (recommendedOrderMap.get(b.sectionType) ?? 999) : 999;
            return orderA - orderB;
          }
          // Both disabled: keep original order
          return 0;
        });
      }

      // Custom image/html templates already carry their own visual CTA/checkout areas.
      // React CTA/guarantee inserts are only for legacy React section templates.

      const allFunnelSections: FunnelSection[] = [];
      let orderCounter = 0;

      if (isHtmlTemplate || isCustomImageTemplate) {
        for (const section of apiSections) {
          allFunnelSections.push({ ...section, order: orderCounter++ });
        }
      } else {
        // Image/React templates: interleave standard sections among design sections
        const CTA1_INSERT_AFTER = 0;
        const WARRANTY_INSERT_AFTER = 1;
        const CTA2_INSERT_AFTER = 3;
        const CTA3_INSERT_AFTER = 5;

        const cta1Section = STANDARD_SECTIONS.find(s => s.id === 'cta-1')!;
        const cta2Section = STANDARD_SECTIONS.find(s => s.id === 'cta-2')!;
        const cta3Section = STANDARD_SECTIONS.find(s => s.id === 'cta-3')!;
        const warrantySection = STANDARD_SECTIONS.find(s => s.id === 'warranty')!;
        const faqSection = STANDARD_SECTIONS.find(s => s.id === 'faq')!;

        for (let i = 0; i < apiSections.length; i++) {
          allFunnelSections.push({ ...apiSections[i], order: orderCounter++ });

          if (i === CTA1_INSERT_AFTER && apiSections.length > CTA1_INSERT_AFTER) {
            allFunnelSections.push({ ...cta1Section, order: orderCounter++ });
          }
          if (i === CTA2_INSERT_AFTER && apiSections.length > CTA2_INSERT_AFTER) {
            allFunnelSections.push({ ...cta2Section, order: orderCounter++ });
          }
          if (i === CTA3_INSERT_AFTER && apiSections.length > CTA3_INSERT_AFTER) {
            allFunnelSections.push({ ...cta3Section, order: orderCounter++ });
          }
          if (i === WARRANTY_INSERT_AFTER && apiSections.length > WARRANTY_INSERT_AFTER) {
            allFunnelSections.push({ ...warrantySection, order: orderCounter++ });
          }
        }

        if (apiSections.length <= CTA1_INSERT_AFTER) {
          allFunnelSections.push({ ...cta1Section, order: orderCounter++ });
        }
        if (apiSections.length <= CTA2_INSERT_AFTER) {
          allFunnelSections.push({ ...cta2Section, order: orderCounter++ });
        }
        if (apiSections.length <= CTA3_INSERT_AFTER) {
          allFunnelSections.push({ ...cta3Section, order: orderCounter++ });
        }
        if (apiSections.length <= WARRANTY_INSERT_AFTER) {
          allFunnelSections.push({ ...warrantySection, order: orderCounter++ });
        }

        allFunnelSections.push({ ...faqSection, order: orderCounter++ });
      }

      // Update the funnel config
      set((state) => {
        const funnels = [...state.multiFunnelData.funnels];
        if (funnels[funnelIndex]) {
          const familyMetadata = templateDesign?.familyMetadata ?? null;
          funnels[funnelIndex] = {
            ...funnels[funnelIndex],
            designTemplate: templateId,
            designTemplateType: templateType,
            designTemplateName: templateDesign?.templateName ?? null,
            designTemplatePreviewUrl: templateDesign?.previewUrl ?? null,
            designTheme: templateDesign?.theme ?? null,
            designStyleSheet: templateDesign?.styleSheet ?? null,
            designPalette: templateDesign?.palette ?? null,
            designPrimaryColor: templateDesign?.primaryColor ?? null,
            designSecondaryColor: templateDesign?.secondaryColor ?? null,
            designCoverStyle: templateDesign?.coverStyle ?? null,
            designTemplateContractVersion: templateDesign?.contractVersion ?? null,
            designTemplateFamilyMetadata: familyMetadata,
            universalStyleEngineVersion: null,
            universalCatalogReleaseId: null,
            universalPageStyleId: null,
            universalCreationRequestId: null,
            promotion: isSingleCheckoutQuantityMode(familyMetadata) && funnels[funnelIndex].promotion?.type === 'buy_x_get_y'
              ? null
              : funnels[funnelIndex].promotion,
            sections: allFunnelSections,
          };
        }
        return { multiFunnelData: { ...state.multiFunnelData, funnels }, isFetchingDesignSections: false };
      });
    } catch (error) {
      console.error('Error fetching design sections:', error);
      // Keep the selected card visible, but invalidate any custom sections from
      // the previous template so this funnel cannot be submitted as prepared.
      set((state) => {
        const funnels = [...state.multiFunnelData.funnels];
        if (funnels[funnelIndex]) {
          const familyMetadata = templateDesign?.familyMetadata ?? null;
          funnels[funnelIndex] = {
            ...funnels[funnelIndex],
            designTemplate: templateId,
            designTemplateType: templateType,
            designTemplateName: templateDesign?.templateName ?? null,
            designTemplatePreviewUrl: templateDesign?.previewUrl ?? null,
            designTheme: templateDesign?.theme ?? null,
            designStyleSheet: templateDesign?.styleSheet ?? null,
            designPalette: templateDesign?.palette ?? null,
            designPrimaryColor: templateDesign?.primaryColor ?? null,
            designSecondaryColor: templateDesign?.secondaryColor ?? null,
            designCoverStyle: templateDesign?.coverStyle ?? null,
            designTemplateContractVersion: templateDesign?.contractVersion ?? null,
            designTemplateFamilyMetadata: familyMetadata,
            universalStyleEngineVersion: null,
            universalCatalogReleaseId: null,
            universalPageStyleId: null,
            universalCreationRequestId: null,
            promotion: isSingleCheckoutQuantityMode(familyMetadata) && funnels[funnelIndex].promotion?.type === 'buy_x_get_y'
              ? null
              : funnels[funnelIndex].promotion,
            sections: invalidatePreparedTemplateSections(funnels[funnelIndex].sections),
          };
        }
        return { multiFunnelData: { ...state.multiFunnelData, funnels }, isFetchingDesignSections: false };
      });
    }
  },
  sectionTemplates: [],
  isFetchingTemplates: false,
  hasFetchedTemplates: false,
  onboardingBindings: [],
  isFetchingBindings: false,
  hasFetchedBindings: false,
  fetchSectionTemplates: async () => {
    const state = get();
    if (state.isFetchingTemplates) return;
    if (state.hasFetchedTemplates && state.sectionTemplates.length > 0) return;
    set({ isFetchingTemplates: true });
    try {
      const templates = await getSectionTemplates();
      set({ sectionTemplates: templates, hasFetchedTemplates: true, isFetchingTemplates: false });
    } catch (error) {
      console.error('Error fetching section templates:', error);
      set({ isFetchingTemplates: false });
    }
  },
  fetchOnboardingBindings: async () => {
    const state = get();
    if (state.hasFetchedBindings || state.isFetchingBindings) return;
    set({ isFetchingBindings: true });
    try {
      const bindings = await getOnboardingCards();
      set({ onboardingBindings: bindings, hasFetchedBindings: true, isFetchingBindings: false });
    } catch (error) {
      console.error('Error fetching onboarding bindings:', error);
      set({ isFetchingBindings: false });
    }
  },
  setSalesAngles: (salesAngles: SalesAngle[]) =>
    set({ salesAngles }),
  setHasFetchedSalesAngles: (hasFetchedSalesAngles: boolean) =>
    set({ hasFetchedSalesAngles }),
  updateContextData: (contextData) =>
    set((state) => ({
      contextData: { ...state.contextData, ...contextData }
    })),
  fetchPreviousProducts: async (search = '', page = 0, size = 15) => {
    const state = get();
    const normalizedSearch = search.trim();

    if (normalizedSearch) {
      return state.searchPreviousProducts(normalizedSearch, page, size);
    }
    
    if (state.isPreviousProductsLoading) {
      return state.previousProducts;
    }
    if (
      state.hasFetchedPreviousProducts &&
      state.previousProductsSearch === normalizedSearch &&
      state.previousProductsPage === page
    ) {
      return state.previousProducts;
    }

    set({ isPreviousProductsLoading: true });
    try {
      const response = await axiosInstanceWebsite.get('/store/product', {
        params: {
          page,
          size,
          search: normalizedSearch || undefined,
        },
      });
      const payload = response.data;
      const products = (Array.isArray(payload) ? payload : payload?.items || []) as PreviousProduct[];

      set({ 
        previousProducts: products,
        previousProductsSearch: normalizedSearch,
        previousProductsPage: Array.isArray(payload) ? page : payload?.page ?? page,
        previousProductsTotalPages: Array.isArray(payload) ? 1 : payload?.total_pages ?? 0,
        hasFetchedPreviousProducts: true,
        isPreviousProductsLoading: false
      });
      return products;
    } catch (err) {
      console.error('Error fetching previous products:', err);
      set({ 
        previousProducts: [],
        previousProductsSearch: normalizedSearch,
        previousProductsPage: page,
        previousProductsTotalPages: 0,
        hasFetchedPreviousProducts: false,
        isPreviousProductsLoading: false 
      });
      return [];
    }
  },
  searchPreviousProducts: async (search, page = 0, size = 15) => {
    const normalizedSearch = search.trim();

    if (!normalizedSearch) {
      return get().previousProducts;
    }

    try {
      const response = await axiosInstanceWebsite.get('/store/product', {
        params: {
          page,
          size,
          search: normalizedSearch,
        },
      });
      const payload = response.data;
      return (Array.isArray(payload) ? payload : payload?.items || []) as PreviousProduct[];
    } catch (err) {
      console.error('Error searching previous products:', err);
      return [];
    }
  },
  setPreviousProducts: (products) => set({ previousProducts: products }),
  // Design structure recommendation
  designRecommendation: null,
  isFetchingDesignRecommendation: false,
  hasFetchedDesignRecommendation: false,
  fetchDesignRecommendation: async (templateTypes?: string[], force = false) => {
    const state = get();
    const selectedTemplateTypes = Array.from(new Set((templateTypes ?? []).filter(Boolean)));
    const cachedRecommendation = state.designRecommendation;
    const cachedMatchesSelection =
      selectedTemplateTypes.length === 0 ||
      Boolean(cachedRecommendation?.recommended_template_type && selectedTemplateTypes.includes(cachedRecommendation.recommended_template_type));
    if (state.isFetchingDesignRecommendation && !force) return cachedRecommendation;
    if (!force && state.hasFetchedDesignRecommendation && cachedMatchesSelection) return cachedRecommendation;

    set({ isFetchingDesignRecommendation: true });

    try {
      const { productData } = state.data;
      const { visualContent } = state.data;
      let payload: DesignStructurePayload;

      // If the product was selected from "tus productos", send product_id
      if (productData.uploadedMethod === UploadedMethodType.PREVIOUS && productData.externalId) {
        payload = { product_id: productData.externalId };
      } else {
        // Otherwise send product info (name, description, images)
        payload = {
          product_name: productData.name,
          product_description: productData.details,
          product_images: visualContent.images || [],
        };
      }
      if (selectedTemplateTypes.length > 0) {
        payload.template_types = selectedTemplateTypes;
      }

      const response = await selectDesignStructure(payload);
      set({
        designRecommendation: response,
        isFetchingDesignRecommendation: false,
        hasFetchedDesignRecommendation: true,
      });
      return response;
    } catch (error) {
      console.error('Error fetching design structure recommendation:', error);
      set({
        designRecommendation: null,
        isFetchingDesignRecommendation: false,
        hasFetchedDesignRecommendation: false,
      });
      throw error;
    }
  },
  resetDesignRecommendation: () => set({
    designRecommendation: null,
    isFetchingDesignRecommendation: false,
    hasFetchedDesignRecommendation: false,
  }),
  // Sales angle recommendation
  salesAngleRecommendation: null,
  isFetchingSalesAngleRecommendation: false,
  hasFetchedSalesAngleRecommendation: false,
  fetchSalesAngleRecommendation: async (rawAngles: { name: string; description: string; complementary_angles?: string[] }[]) => {
    const state = get();
    if (state.isFetchingSalesAngleRecommendation || state.hasFetchedSalesAngleRecommendation) return;

    // If design recommendation hasn't been fetched yet, wait for it (it's required by the API).
    // If it's currently being fetched, poll until done; if it hasn't started, trigger it.
    if (!state.hasFetchedDesignRecommendation) {
      if (!state.isFetchingDesignRecommendation) {
        // Trigger the fetch — it may not have started yet
        await state.fetchDesignRecommendation();
      } else {
        // It's in-flight — wait for it to finish (poll with short intervals)
        await new Promise<void>((resolve) => {
          const check = () => {
            if (get().hasFetchedDesignRecommendation) {
              resolve();
            } else {
              setTimeout(check, 200);
            }
          };
          check();
        });
      }
    }

    // Re-read state after design recommendation is available
    const freshState = get();

    // If design recommendation is still null after fetching (e.g. API error), skip gracefully —
    // the backend requires design_structure and would reject null.
    if (!freshState.designRecommendation) {
      console.warn('Skipping sales angle recommendation — design_structure not available');
      set({
        isFetchingSalesAngleRecommendation: false,
        hasFetchedSalesAngleRecommendation: true,
      });
      return;
    }

    set({ isFetchingSalesAngleRecommendation: true });

    try {
      const { productData, visualContent } = freshState.data;

      const payload = {
        angles: rawAngles,
        design_structure: freshState.designRecommendation,
        product_name: productData.name,
        product_description: productData.details,
        product_images: visualContent.images || [],
      };

      const response = await selectSalesAngle(payload);

      // Auto-select the recommended angle in visualContent.selectedAngle
      const matchingAngle = freshState.salesAngles.find(
        (a) => a.id === response.selected_angle || a.title === response.selected_angle
      );

      if (matchingAngle) {
        set((s) => ({
          salesAngleRecommendation: response,
          isFetchingSalesAngleRecommendation: false,
          hasFetchedSalesAngleRecommendation: true,
          data: {
            ...s.data,
            visualContent: {
              ...s.data.visualContent,
              selectedAngle: [{
                name: matchingAngle.id,
                description: matchingAngle.description,
                complementary_angles: matchingAngle.complementary_angles || [],
              }],
            },
          },
        }));
      } else {
        set({
          salesAngleRecommendation: response,
          isFetchingSalesAngleRecommendation: false,
          hasFetchedSalesAngleRecommendation: true,
        });
      }
    } catch (error) {
      console.error('Error fetching sales angle recommendation:', error);
      set({
        isFetchingSalesAngleRecommendation: false,
        hasFetchedSalesAngleRecommendation: true,
      });
    }
  },
  // Ad creative templates
  adTemplates: [],
  isFetchingAdTemplates: false,
  hasFetchedAdTemplates: false,
  isRankingTemplates: false,
  hasRankedTemplates: false,
  rankedTemplateProductKey: null,
  isRankingPageTemplates: false,
  hasRankedPageTemplates: false,
  rankedPageTemplateProductKey: null,
  isRankingAdTemplates: false,
  hasRankedAdTemplates: false,
  rankedAdTemplateProductKey: null,
  fetchAdTemplates: async () => {
    const state = get();
    if (state.hasFetchedAdTemplates || state.isFetchingAdTemplates) return;
    set({ isFetchingAdTemplates: true });
    try {
      const templates = await templatesService.getTemplates();
      // Filter out custom upload entries — not relevant for onboarding
      const filtered = templates.filter(t => !t.isCustomUpload);
      set({ adTemplates: filtered, hasFetchedAdTemplates: true, isFetchingAdTemplates: false });
    } catch (error) {
      console.error('Error fetching ad templates:', error);
      // Fall back to mock data so the tab is never empty
      const fallback = MOCK_STYLES.filter(t => !t.isCustomUpload);
      set({ adTemplates: fallback, hasFetchedAdTemplates: true, isFetchingAdTemplates: false });
    }
  },
  rankOnboardingTemplates: async () => {
    const state = get();
    const { productData } = state.data;
    const productKey = `${productData.name || ''}::${productData.details || ''}`;
    if (!productData.name) {
      set({
        isRankingTemplates: false,
        hasRankedTemplates: true,
        rankedTemplateProductKey: productKey,
        hasRankedPageTemplates: true,
        rankedPageTemplateProductKey: productKey,
        hasRankedAdTemplates: true,
        rankedAdTemplateProductKey: productKey,
      });
      return;
    }

    type SourceProductMetadata = {
      source_product?: {
        title?: string;
        summary_10_words?: string;
        description?: string;
      };
    };
    const sourceProduct = (metadata?: SourceProductMetadata | null) => metadata?.source_product || {};
    const withoutLeadingLabel = (value?: string) => {
      const text = value || '';
      const colonIndex = text.indexOf(':');
      if (colonIndex < 0 || colonIndex > 24) return text;
      return text.slice(colonIndex + 1).trim() || text;
    };
    const compactSourceProduct = (metadata?: SourceProductMetadata | null) => {
      const source = sourceProduct(metadata);
      return {
        title: source.title || '',
        summary: withoutLeadingLabel(source.summary_10_words || source.description || ''),
      };
    };
    const product = {
      name: productData.name,
      description: productData.details,
    };

    const pageTemplates = state.onboardingBindings
      .filter(isSelectableOnboardingCard)
      .map((binding) => ({
        id: getOnboardingBindingOptionId(binding),
        source_product: {
          title: binding.display_name || '',
          summary: binding.description || '',
        },
      }));
    const adTemplates = state.adTemplates.filter(t => !t.isCustomUpload).map((t) => ({
      id: String(t.name_id || t.id),
      source_product: compactSourceProduct(t.metadata as SourceProductMetadata | null),
    }));

    if (
      pageTemplates.length === 0 &&
      state.hasFetchedBindings &&
      !state.isFetchingBindings &&
      !(state.hasRankedPageTemplates && state.rankedPageTemplateProductKey === productKey)
    ) {
      set({ hasRankedPageTemplates: true, rankedPageTemplateProductKey: productKey });
    }
    if (
      adTemplates.length === 0 &&
      state.hasFetchedAdTemplates &&
      !state.isFetchingAdTemplates &&
      !(state.hasRankedAdTemplates && state.rankedAdTemplateProductKey === productKey)
    ) {
      set({ hasRankedAdTemplates: true, rankedAdTemplateProductKey: productKey });
    }

    const shouldRankPages =
      pageTemplates.length > 0 &&
      !state.isRankingPageTemplates &&
      !(state.hasRankedPageTemplates && state.rankedPageTemplateProductKey === productKey);
    const shouldRankAds =
      adTemplates.length > 0 &&
      !state.isRankingAdTemplates &&
      !(state.hasRankedAdTemplates && state.rankedAdTemplateProductKey === productKey);

    if (!shouldRankPages && !shouldRankAds) {
      const current = get();
      set({
        isRankingTemplates: current.isRankingPageTemplates || current.isRankingAdTemplates,
        hasRankedTemplates:
          current.hasRankedPageTemplates &&
          current.rankedPageTemplateProductKey === productKey &&
          current.hasRankedAdTemplates &&
          current.rankedAdTemplateProductKey === productKey,
        rankedTemplateProductKey: productKey,
      });
      return;
    }

    const refreshAggregateRankingState = () => {
      const current = get();
      set({
        isRankingTemplates: current.isRankingPageTemplates || current.isRankingAdTemplates,
        hasRankedTemplates:
          current.hasRankedPageTemplates &&
          current.rankedPageTemplateProductKey === productKey &&
          current.hasRankedAdTemplates &&
          current.rankedAdTemplateProductKey === productKey,
        rankedTemplateProductKey: productKey,
      });
    };

    const rankPages = async () => {
      set({ isRankingTemplates: true, isRankingPageTemplates: true });
      const response = await axiosInstanceConversationalEngine.post('/onboarding/template-ranking', {
        product,
        page_templates: pageTemplates,
        ad_templates: [],
      });

      const pageIds = response.data?.page_template_ids || [];
      const pageOrder = new Map<string, number>(pageIds.map((id: string | number, i: number) => [String(id), i]));

      set((s) => ({
        onboardingBindings: [...s.onboardingBindings].sort((a, b) =>
          (pageOrder.get(getOnboardingBindingOptionId(a)) ?? 9999) -
          (pageOrder.get(getOnboardingBindingOptionId(b)) ?? 9999)
        ),
        isRankingPageTemplates: false,
        hasRankedPageTemplates: true,
        rankedPageTemplateProductKey: productKey,
      }));
      refreshAggregateRankingState();
    };

    const rankAds = async () => {
      set({ isRankingTemplates: true, isRankingAdTemplates: true });
      const response = await axiosInstanceConversationalEngine.post('/onboarding/template-ranking', {
        product,
        page_templates: [],
        ad_templates: adTemplates,
      });

      const adIds = response.data?.ad_template_ids || [];
      const adOrder = new Map<string, number>(adIds.map((id: string | number, i: number) => [String(id), i]));

      set((s) => ({
        adTemplates: [...s.adTemplates].sort((a, b) => {
          if (a.isCustomUpload) return 1;
          if (b.isCustomUpload) return -1;
          return (adOrder.get(a.name_id || a.id) ?? 9999) - (adOrder.get(b.name_id || b.id) ?? 9999);
        }),
        isRankingAdTemplates: false,
        hasRankedAdTemplates: true,
        rankedAdTemplateProductKey: productKey,
      }));
      refreshAggregateRankingState();
    };

    const rankingJobs = [];
    if (shouldRankPages) {
      rankingJobs.push(
        rankPages().catch((error) => {
          console.warn('Page template ranking unavailable, keeping default order', error);
          set({
            isRankingPageTemplates: false,
            hasRankedPageTemplates: true,
            rankedPageTemplateProductKey: productKey,
          });
          refreshAggregateRankingState();
        })
      );
    }
    if (shouldRankAds) {
      rankingJobs.push(
        rankAds().catch((error) => {
          console.warn('Ad template ranking unavailable, keeping default order', error);
          set({
            isRankingAdTemplates: false,
            hasRankedAdTemplates: true,
            rankedAdTemplateProductKey: productKey,
          });
          refreshAggregateRankingState();
        })
      );
    }

    await Promise.allSettled(rankingJobs);
  },
  toggleFunnelAdTemplate: (funnelIndex: number, templateId: string) => set((state) => {
    const funnels = [...state.multiFunnelData.funnels];
    if (!funnels[funnelIndex]) return state;
    const current = funnels[funnelIndex].selectedAdTemplates || [];
    const isSelected = current.includes(templateId);
    const maxTemplates = 10;
    let updated: string[];
    if (isSelected) {
      updated = current.filter(id => id !== templateId);
    } else if (current.length < maxTemplates) {
      updated = [...current, templateId];
    } else {
      return state;
    }
    funnels[funnelIndex] = { ...funnels[funnelIndex], selectedAdTemplates: updated };
    return { multiFunnelData: { ...state.multiFunnelData, funnels } };
  }),
  // Legacy template selection (multiple)
  selectedLegacyTemplates: [],
  toggleLegacyTemplate: (template) => {
    invalidateEstimatedCostRequest();
    set((state) => {
      const exists = state.selectedLegacyTemplates.find(t => t.id === template.id);
      if (exists) {
        return {
          selectedLegacyTemplates: state.selectedLegacyTemplates.filter(t => t.id !== template.id),
          estimatedCost: null,
          isFetchingEstimatedCost: false,
        };
      }
      return {
        selectedLegacyTemplates: [...state.selectedLegacyTemplates, template],
        estimatedCost: null,
        isFetchingEstimatedCost: false,
      };
    });
  },
  clearLegacyTemplates: () => {
    invalidateEstimatedCostRequest();
    set({
      selectedLegacyTemplates: [],
      estimatedCost: null,
      isFetchingEstimatedCost: false,
    });
  },
  selectedOnboardingTemplateIds: [],
  setSelectedOnboardingTemplateIds: (templateIds) => {
    invalidateEstimatedCostRequest();
    set({
      selectedOnboardingTemplateIds: [...templateIds],
      estimatedCost: null,
      isFetchingEstimatedCost: false,
    });
  },
  estimatedCost: null,
  isFetchingEstimatedCost: false,
  fetchEstimatedCost: async () => {
    const state = get();
    if (state.isFetchingEstimatedCost) return;
    const requestGeneration = ++estimatedCostRequestGeneration;
    set({ isFetchingEstimatedCost: true });
    try {
      const funnels = state.multiFunnelData.funnels;
      const creativesCount = funnels.reduce(
        (sum, f) => sum + (f.selectedAdTemplates?.length || 0),
        0
      );
      const [creativeEstimate, websiteEstimate] = await Promise.all([
        creativesService.estimateCost(creativesCount, 0),
        postBulkFunnelsEstimate({
          products: [],
          funnels: funnels.map((funnel) => ({
            brand_name: state.data.visualStyle.brandName || state.data.productData.name || 'Marca',
            sale_angles: funnel.selectedAngles.length
              ? funnel.selectedAngles
              : [{ name: '', description: '', complementary_angles: [] }],
            free_shipping: state.data.productData.freeShipping,
            country_code: funnel.countryCode,
            currency_code: funnel.currencyCode,
            warranties: 0,
            is_custom_sections_only: true,
            ...(funnel.includeVideoReviewsGifs
              ? {
                  template_structure: {
                    template_pages: [{
                      id: 'video_reviews_estimate',
                      name: 'video_reviews_estimate',
                      type: 'customCodeSection',
                      sort: 1,
                      page: 'productPage',
                      source: 'generate_video_reviews',
                      metadata: { section_role: 'video_reviews' },
                    }],
                  },
                }
              : {}),
          })),
          data_for_context: {
            pain_detection: state.contextData.pain_detection,
            buyer_detection: state.contextData.buyer_detection,
            recommended_sales_angles: state.contextData.recommended_sales_angles,
          },
        }),
      ]);
      if (requestGeneration !== estimatedCostRequestGeneration) return;
      set({
        estimatedCost: {
          total_cost: creativeEstimate.creatives_cost + websiteEstimate.total_credits,
          funnels_cost: websiteEstimate.base_credits,
          creatives_cost: creativeEstimate.creatives_cost,
          video_reviews_gifs_cost: websiteEstimate.video_reviews_gifs_credits,
          video_reviews_gifs_credits_per_funnel:
            websiteEstimate.credits_per_video_reviews_gifs_page ||
            ONBOARDING_VIDEO_REVIEWS_GIFS_CREDITS_PER_FUNNEL,
        },
        isFetchingEstimatedCost: false,
      });
    } catch (error) {
      if (requestGeneration !== estimatedCostRequestGeneration) return;
      console.error('Error fetching estimated cost:', error);
      const funnels = state.multiFunnelData.funnels;
      const creativesCount = funnels.reduce(
        (sum, f) => sum + (f.selectedAdTemplates?.length || 0),
        0
      );
      const funnelsCount = funnels.length;
      const videoReviewsGifsCost = getOnboardingVideoReviewsGifsCredits(funnels);
      set({ isFetchingEstimatedCost: false });
      try {
        const result = await creativesService.estimateCost(creativesCount, funnelsCount);
        if (requestGeneration !== estimatedCostRequestGeneration) return;
        set({
          estimatedCost: {
            ...result,
            total_cost: result.total_cost + videoReviewsGifsCost,
            video_reviews_gifs_cost: videoReviewsGifsCost,
            video_reviews_gifs_credits_per_funnel: ONBOARDING_VIDEO_REVIEWS_GIFS_CREDITS_PER_FUNNEL,
          },
        });
      } catch {
        // The estimate is informational; final credit validation still happens in ecommerce.
      }
    }
  },
}));
